export interface JustifiedItemInput {
  id: string
  width: number
  height: number
}

export interface JustifiedTile {
  id: string
  width: number
  height: number
}

export interface JustifiedRow {
  tiles: Array<JustifiedTile>
  height: number
  top: number
  /** false only for the trailing, not-full row (left-aligned, natural widths) */
  justified: boolean
}

export interface JustifiedLayoutOptions {
  containerWidth: number
  targetRowHeight: number
  gap: number
  maxAspect?: number
  minAspect?: number
}

export interface JustifiedLayoutResult {
  rows: Array<JustifiedRow>
  totalHeight: number
}

const DEFAULT_MAX_ASPECT = 3.5
const DEFAULT_MIN_ASPECT = 0.4

const clampAspect = (item: JustifiedItemInput, min: number, max: number): number => {
  const aspect = item.width / item.height
  if (!Number.isFinite(aspect) || aspect <= 0) return 1
  return Math.min(max, Math.max(min, aspect))
}

export const computeJustifiedLayout = (
  items: Array<JustifiedItemInput>,
  options: JustifiedLayoutOptions
): JustifiedLayoutResult => {
  const { containerWidth, targetRowHeight, gap } = options
  const maxAspect = options.maxAspect ?? DEFAULT_MAX_ASPECT
  const minAspect = options.minAspect ?? DEFAULT_MIN_ASPECT

  if (containerWidth <= 0 || items.length === 0) {
    return { rows: [], totalHeight: 0 }
  }

  const rows: Array<JustifiedRow> = []
  let top = 0
  let rowItems: Array<{ id: string; aspect: number }> = []
  let rowAspectSum = 0

  const rowHeightFor = (aspectSum: number, count: number): number =>
    (containerWidth - gap * (count - 1)) / aspectSum

  // Justified rows fill the container exactly: float widths are rounded cumulatively and
  // the last tile absorbs the remainder, so Σwidths + gaps === containerWidth.
  const flushRow = (rawHeight: number, justified: boolean) => {
    const height = Math.max(1, Math.round(rawHeight))
    const innerWidth = containerWidth - gap * (rowItems.length - 1)
    const tiles: Array<JustifiedTile> = []
    let idealRight = 0
    let assigned = 0
    for (let i = 0; i < rowItems.length; i++) {
      let width: number
      if (justified && i === rowItems.length - 1) {
        width = innerWidth - assigned
      } else {
        idealRight += rowItems[i].aspect * rawHeight
        width = Math.round(Math.min(idealRight, innerWidth)) - assigned
      }
      width = Math.max(1, width)
      assigned += width
      tiles.push({ id: rowItems[i].id, width, height })
    }
    rows.push({ tiles, height, top, justified })
    top += height + gap
    rowItems = []
    rowAspectSum = 0
  }

  for (const item of items) {
    const aspect = clampAspect(item, minAspect, maxAspect)

    if (rowItems.length > 0) {
      const heightWith = rowHeightFor(rowAspectSum + aspect, rowItems.length + 1)
      if (heightWith < targetRowHeight) {
        const heightWithout = rowHeightFor(rowAspectSum, rowItems.length)
        // Flush whichever variant lands closer to the target height (Flickr heuristic)
        if (
          Math.abs(heightWithout - targetRowHeight) <= Math.abs(heightWith - targetRowHeight)
        ) {
          flushRow(heightWithout, true)
        }
      }
    }

    rowItems.push({ id: item.id, aspect })
    rowAspectSum += aspect

    const height = rowHeightFor(rowAspectSum, rowItems.length)
    if (height <= targetRowHeight) {
      flushRow(height, true)
    }
  }

  if (rowItems.length > 0) {
    const height = rowHeightFor(rowAspectSum, rowItems.length)
    flushRow(Math.min(height, targetRowHeight), false)
  }

  return { rows, totalHeight: Math.max(0, top - gap) }
}
